import type { BlogPost } from "../types";
import greenwall from "@/assets/greenwall.jpg";
import roomDining from "@/assets/room-dining.jpg";
import roomOffice from "@/assets/room-office.jpg";

export const sampleBlogPosts: BlogPost[] = [
  {
    id: "post-1",
    title: "Styling Artificial Palms in Low-Light Apartments",
    slug: "styling-artificial-palms-in-low-light-apartments",
    excerpt:
      "How to position architectural greenery in north-facing living rooms and dark hallways for maximum natural visual impact.",
    content: `
North-facing apartments and windowless hallways have long presented a challenge for interior plant lovers. While real parlor palms and monsteras quickly succumb to lack of sunlight, high-grade artificial greenery offers an elegant, permanent solution.

### 1. Scale & Proportion with Statement Trees
When styling a low-light corner, scale is your most critical design lever. Placing one of our [5 FT artificial statement trees](/collections/trees-and-plants)—such as a tall Japanese maple, natural cane bamboo, or a Mediterranean olive tree—creates instant vertical depth and draws the eye upward, giving the illusion of higher ceilings.

### 2. Tabletop Greens & Shelf Accents
For smaller nooks, credenzas, and work desks, incorporate [tabletop faux bonsai trees](/collections/artificial-bonsai) or lush [potted artificial plants](/collections/flower-plants-and-pots) like split-leaf monsteras, caladiums, and purple calatheas. Each piece arrives already seated in a designer ceramic pot, ensuring clean lines and balanced visual weight without needing replanting.

### 3. Vertical Wall Drama with Trailing Vines
Bare walls, room dividers, and high bookshelves benefit immensely from cascading greenery. Adding [cascading artificial floral vines](/collections/hanging-plants) along a floating shelf softens harsh architectural angles with natural, lifelike drape and subtle blossom clusters.

### 4. Layering Ambient Light
Even though artificial botanicals require no natural sunlight to survive, subtle ambient lighting—such as a warm floor lamp or a hidden picture light—highlights leaf venation and shadow play, making the arrangement look vibrant, organic, and alive. Browse our full catalogue of [artificial flowers and fake plants online](/shop) to find the perfect botanical accent for your interior.
    `,
    coverImage: roomOffice,
    authorName: "Elena Vance",
    publishedAt: "July 15, 2026",
    readingTime: "4 min read",
    category: "Styling Guides",
  },
  {
    id: "post-2",
    title: "The Craftsmanship of Reclaimed Timber Trunks",
    slug: "craftsmanship-of-reclaimed-timber-trunks",
    excerpt:
      "A look inside our Copenhagen and Kyoto workshops: how real fallen wood is selected and hand-fitted with silk foliage.",
    content: `
At Aster Decorz, we believe that the soul of an artificial tree lies in its trunk. Plastic molded trunks often look synthetic because nature rarely repeats exact geometry.

### Hand-Selected Timber & Real Wood Trunks
Every wooden stem used in our [5 FT artificial statement trees](/collections/trees-and-plants) is harvested from sustainably thinned forests and reclaimed timber. Each trunk features natural knots, grain variations, and subtle curves that give artificial foliage the authentic presence of mature growth.

### Handcrafted Bonsai Sculpture
This dedication to form is equally evident in our [tabletop faux bonsai trees](/collections/artificial-bonsai). Ranging from delicate pink cherry blossoms to rugged Japanese pine and cedar bonsai, each piece is individually sculpted with textured bark and nestled into fluted stoneware dishes.

### Blooming Color with Flower Bushes
For interior schemes needing vibrant pops of colour, our [artificial flower bushes](/collections/flower-bushes) combine dense silk petals with natural stem arrangements. From purple bougainvilleas to trumpet lilies, they provide constant springtime bloom on dining tables and mantels without wilting.

### Leaf Attachment Technique
Our artisans hand-insert silk stem branches into pre-drilled channels in the wood, securing them with volcanic stone filler and resin. This ensures structural strength while maintaining an organic, irregular growth silhouette. Explore our [curated artificial plant collections](/collections) to see how craftsmanship transforms faux greenery.
    `,
    coverImage: roomDining,
    authorName: "Lukas Holm",
    publishedAt: "June 28, 2026",
    readingTime: "5 min read",
    category: "Craft & Materials",
  },
  {
    id: "post-3",
    title: "Preserved Moss Walls for Acoustic Calm",
    slug: "preserved-moss-walls-for-acoustic-calm",
    excerpt:
      "Discover how modular reindeer lichen and pole moss panels absorb sound while creating biophilic sanctuary spaces.",
    content: `
Acoustic comfort is one of the most overlooked elements of luxury interior design. Hard surfaces like marble, glass, and polished concrete bounce sound waves around a room, creating echo and noise fatigue.

### Natural Sound Absorption
Preserved moss panels act as natural acoustic dampeners. The porous structure of pole moss and reindeer lichen breaks down sound waves, reducing reverberation by up to 35% in open-plan offices and dining nooks. Pairing wall greenery with [cascading artificial floral vines](/collections/hanging-plants) creates a layered, biophilic wall installation that softens both sound and room acoustics.

### Zero Maintenance Biophilia
Unlike living green walls which require complex irrigation systems and regular pruning, preserved moss and luxury faux plants undergo careful preservation. They retain their soft texture and vibrant emerald colour without a drop of water.

### Completing the Interior Ambience
To complement wall acoustic elements, balance the room with tabletop arrangements. Ready-to-arrange [artificial flower bunches for vases](/collections/flower-bunches) or sculptural [5 FT artificial statement trees](/collections/trees-and-plants) in corners absorb harsh reflections and transform sterile interiors into serene botanical sanctuaries. Visit our [Aster Decorz online shop](/shop) to explore the complete collection.
    `,
    coverImage: greenwall,
    authorName: "Sora Takahashi",
    publishedAt: "June 10, 2026",
    readingTime: "6 min read",
    category: "Commercial Interiors",
  },
];

export async function getBlogPosts(): Promise<BlogPost[]> {
  return sampleBlogPosts;
}

export async function getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  return sampleBlogPosts.find((p) => p.slug === slug) || null;
}
